import React, {useCallback, useEffect, useState} from "react";
import {Button, DatePicker, Modal, Select, Tag} from "antd";
import {message} from "../../utils/antdFeedback";
import {ReceiptText} from "lucide-react";
import {useDispatch, useSelector} from "react-redux";
import {useNavigate} from "react-router-dom";
import {AdminLayout} from "../../components/layout/AdminLayout";
import {GenericTable} from "../../components/table/GenericTable";
import {AppDispatch, RootState} from "../../redux/store";
import {getBillPayments, refundFailedBillPayment, retryFailedBillPayment} from "../../redux/slice/billPaymentSlice";
import {HeroHeader, Card, SearchField, formatNaira, COLORS} from "../../designSystem";

const {RangePicker} = DatePicker;

const statusOptions = [
  {label: "All statuses", value: ""},
  {label: "Pending", value: 1},
  {label: "Successful", value: 2},
  {label: "Failed", value: 3},
];

const sourceOptions = [
  {label: "All sources", value: ""},
  {label: "Wallet", value: 1},
  {label: "VFD transfer", value: 2},
];

const statusLabel = (value: any) => {
  const status = Number(value);
  if (status === 2) return "Successful";
  if (status === 3) return "Failed";
  return "Pending";
};

const statusColor = (value: any) => {
  const status = Number(value);
  if (status === 2) return "green";
  if (status === 3) return "red";
  return "gold";
};

const sourceLabel = (value: any) => Number(value) === 1 ? "Wallet" : "VFD transfer";
const moneyCell = (value: any) => <span className="font-mono">{formatNaira(value)}</span>;
const dateCell = (value: string) => value ? new Date(value).toLocaleString() : "-";
const errorText = (error: any) =>
  error?.message || error?.Message || error?.error?.message || "Request failed. Please try again.";

function BillPayments() {
  const dispatch: AppDispatch = useDispatch();
  const navigate = useNavigate();
  const {token} = useSelector((state: RootState) => state.account);
  const {payments, summary, pageInfo, loading} = useSelector((state: RootState) => state.billPayments);
  const [searchParam, setSearchParam] = useState("");
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState<any>("");
  const [source, setSource] = useState<any>("");
  const [dateRange, setDateRange] = useState<any>(null);
  const [refundingId, setRefundingId] = useState<string | null>(null);
  const [retryingId, setRetryingId] = useState<string | null>(null);

  const load = useCallback((pageNumber = 1) => {
    if (!token) return;

    dispatch(getBillPayments({
      token,
      searchParam,
      email,
      status,
      source,
      startDate: dateRange?.[0]?.startOf("day")?.toISOString(),
      endDate: dateRange?.[1]?.endOf("day")?.toISOString(),
      pageNumber,
    }));
  }, [token, dispatch, searchParam, email, status, source, dateRange]);

  useEffect(() => {
    load(1);
  }, [load]);

  const canRefund = (row: any) =>
    Number(row?.status) !== 2 &&
    Number(row?.source) === 1 &&
    row?.walletTransactionId &&
    !row?.adminRefunded;

  const canRetry = (row: any) =>
    Number(row?.status) === 3 &&
    !row?.adminRefunded;

  const refundPayment = (row: any) => {
    if (!token || !row?.id || !canRefund(row)) return;

    Modal.confirm({
      title: "Refund failed bill payment",
      content: "This will credit the unsuccessful bill amount back to the user's wallet. Continue?",
      okText: "Refund wallet",
      onOk: async () => {
        setRefundingId(row.id);
        try {
          await dispatch(refundFailedBillPayment({
            token,
            transactionId: row.id,
            reason: "Admin refund for failed bill payment",
          })).unwrap();
          message.success("Bill payment refunded to wallet");
          load(pageInfo?.pageNumber || 1);
        } catch (error: any) {
          message.error(errorText(error));
        } finally {
          setRefundingId(null);
        }
      },
    });
  };

  const retryPayment = (row: any) => {
    if (!token || !row?.id || !canRetry(row)) return;

    Modal.confirm({
      title: "Retry failed bill payment",
      content: "This will submit the bill payment to VFD again using the same PayHive reference. Continue?",
      okText: "Retry payment",
      onOk: async () => {
        setRetryingId(row.id);
        try {
          await dispatch(retryFailedBillPayment({
            token,
            transactionId: row.id,
          })).unwrap();
          message.success("Bill payment retry submitted");
          load(pageInfo?.pageNumber || 1);
        } catch (error: any) {
          message.error(errorText(error));
        } finally {
          setRetryingId(null);
        }
      },
    });
  };

  return (
    <AdminLayout>
      <main className="h-full overflow-y-auto px-6 py-8 md:px-8" style={{background: COLORS.surface}}>
        <HeroHeader
          icon={ReceiptText}
          title="Bill Payments"
          subtitle={`${summary?.totalRecords || 0} records · ${formatNaira(summary?.totalAmount || 0)} total volume`}
        />

        <div className="grid gap-4 md:grid-cols-3 mt-8">
          <Card className="p-5">
            <div className="text-sm text-slate-500">Successful</div>
            <div className="mt-2 text-2xl font-bold" style={{color: COLORS.brand}}>{formatNaira(summary?.successfulAmount || 0)}</div>
            <div className="text-sm text-slate-500">{summary?.successfulCount || 0} payments</div>
          </Card>
          <Card className="p-5">
            <div className="text-sm text-slate-500">Pending</div>
            <div className="mt-2 text-2xl font-bold" style={{color: COLORS.warning}}>{formatNaira(summary?.pendingAmount || 0)}</div>
            <div className="text-sm text-slate-500">{summary?.pendingCount || 0} payments</div>
          </Card>
          <Card className="p-5">
            <div className="text-sm text-slate-500">Failed</div>
            <div className="mt-2 text-2xl font-bold" style={{color: COLORS.danger}}>{formatNaira(summary?.failedAmount || 0)}</div>
            <div className="text-sm text-slate-500">{summary?.failedCount || 0} payments</div>
          </Card>
        </div>

        <Card className="mt-8 p-5">
          <div className="grid gap-3 md:grid-cols-5">
            <SearchField
              className="md:col-span-2"
              placeholder="Search reference, biller, customer"
              value={searchParam}
              onChange={(event) => setSearchParam(event.target.value)}
            />
            <SearchField
              placeholder="User email"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
            />
            <Select options={statusOptions} value={status} onChange={setStatus}/>
            <Select options={sourceOptions} value={source} onChange={setSource}/>
          </div>
          <div className="mt-3">
            <RangePicker onChange={setDateRange} className="w-full md:w-auto"/>
          </div>

          <GenericTable
            loading={loading}
            pageInfo={pageInfo}
            data={payments}
            handlePageChange={load}
            columns={[
              {title: "Reference", dataIndex: "reference", key: "reference"},
              {title: "User", dataIndex: "userEmail", key: "userEmail"},
              {title: "Biller", key: "biller", render: (_: any, row: any) => row.billerName || row.billerId},
              {title: "Customer", key: "customer", render: (_: any, row: any) => row.customerId || row.customerPhone || "-"},
              {title: "Amount", dataIndex: "amount", key: "amount", render: moneyCell},
              {title: "Source", dataIndex: "source", key: "source", render: (value: any) => <Tag>{sourceLabel(value)}</Tag>},
              {title: "Status", dataIndex: "status", key: "status", render: (value: any) => <Tag color={statusColor(value)}>{statusLabel(value)}</Tag>},
              {title: "Provider", key: "provider", render: (_: any, row: any) => row.providerStatus || row.providerMessage || "-"},
              {title: "Created", dataIndex: "createdAt", key: "createdAt", render: dateCell},
              {title: "Action", key: "action", render: (_: any, row: any) => (
                <div className="flex flex-wrap gap-2">
                  <button
                    onClick={() => navigate(`/bill-payments/${row.id}`)}
                    className="font-semibold"
                    style={{color: COLORS.brand}}
                  >
                    View
                  </button>
                  {row?.adminRefunded ? (
                    <Tag color="green">Refunded</Tag>
                  ) : canRefund(row) ? (
                    <Button
                      size="small"
                      type="primary"
                      loading={refundingId === row.id}
                      disabled={!!refundingId}
                      onClick={() => refundPayment(row)}
                    >
                      Refund wallet
                    </Button>
                  ) : null}
                  {canRetry(row) ? (
                    <Button
                      size="small"
                      loading={retryingId === row.id}
                      disabled={!!retryingId}
                      onClick={() => retryPayment(row)}
                    >
                      Retry
                    </Button>
                  ) : null}
                </div>
              )},
            ]}
          />
        </Card>
      </main>
    </AdminLayout>
  );
}

export default BillPayments;
